import { defineMessages } from 'react-intl';

/**
 * The copy of the TikTok settings section (design music24-composer-tiktok a1 to a6). The section
 * follows TikTok's Content Sharing Guidelines for Direct Post: the names of the privacy levels,
 * the label lines and the consent line say what TikTok says.
 */
export const tiktokMessages = defineMessages({
  title: { id: 'app.sml.promote.composer.tiktok.title', defaultMessage: 'TikTok settings' },
  tag: { id: 'app.sml.promote.composer.tiktok.tag', defaultMessage: 'Direct post' },
  postingTo: { id: 'app.sml.promote.composer.tiktok.postingTo', defaultMessage: 'Posting to' },
  fromTikTok: {
    id: 'app.sml.promote.composer.tiktok.fromTikTok',
    defaultMessage: 'From TikTok, just now',
  },
  loading: { id: 'app.sml.promote.composer.tiktok.loading', defaultMessage: 'Reading your TikTok account…' },
  privacyLabel: { id: 'app.sml.promote.composer.tiktok.privacy.label', defaultMessage: 'Who can see this post' },
  privacyPlaceholder: {
    id: 'app.sml.promote.composer.tiktok.privacy.placeholder',
    defaultMessage: 'Choose who can see it',
  },
  privacyPublic: { id: 'app.sml.promote.composer.tiktok.privacy.public', defaultMessage: 'Everyone' },
  privacyFriends: { id: 'app.sml.promote.composer.tiktok.privacy.friends', defaultMessage: 'Friends' },
  privacyFollowers: { id: 'app.sml.promote.composer.tiktok.privacy.followers', defaultMessage: 'Followers' },
  privacyOnlyMe: { id: 'app.sml.promote.composer.tiktok.privacy.onlyMe', defaultMessage: 'Only me' },
  privacyBrandedPrivate: {
    id: 'app.sml.promote.composer.tiktok.privacy.brandedPrivate',
    defaultMessage: 'Branded content cannot be private.',
  },
  interactionsLabel: { id: 'app.sml.promote.composer.tiktok.interactions', defaultMessage: 'Let people' },
  allowComment: { id: 'app.sml.promote.composer.tiktok.allowComment', defaultMessage: 'Allow comments' },
  allowDuet: { id: 'app.sml.promote.composer.tiktok.allowDuet', defaultMessage: 'Allow Duet' },
  allowStitch: { id: 'app.sml.promote.composer.tiktok.allowStitch', defaultMessage: 'Allow Stitch' },
  offInTikTok: {
    id: 'app.sml.promote.composer.tiktok.offInTikTok',
    defaultMessage: 'Off in your TikTok settings',
  },
  discloseTitle: { id: 'app.sml.promote.composer.tiktok.disclose.title', defaultMessage: 'Disclose post content' },
  discloseNote: {
    id: 'app.sml.promote.composer.tiktok.disclose.note',
    defaultMessage: 'Turn on when this post promotes yourself, a brand or a product.',
  },
  yourBrandTitle: { id: 'app.sml.promote.composer.tiktok.yourBrand.title', defaultMessage: 'Your brand' },
  yourBrandNote: {
    id: 'app.sml.promote.composer.tiktok.yourBrand.note',
    defaultMessage: 'You promote yourself or your own business. TikTok labels the post "Promotional content".',
  },
  brandedTitle: { id: 'app.sml.promote.composer.tiktok.branded.title', defaultMessage: 'Branded content' },
  brandedNote: {
    id: 'app.sml.promote.composer.tiktok.branded.note',
    defaultMessage: 'You promote another brand or a third party. TikTok labels the post "Paid partnership".',
  },
  labelLine: { id: 'app.sml.promote.composer.tiktok.labelLine', defaultMessage: 'Your video will be labeled' },
  labelPromotional: {
    id: 'app.sml.promote.composer.tiktok.label.promotional',
    defaultMessage: 'Promotional content',
  },
  labelPaid: { id: 'app.sml.promote.composer.tiktok.label.paid', defaultMessage: 'Paid partnership' },
  disclosureHint: {
    id: 'app.sml.promote.composer.tiktok.disclose.hint',
    defaultMessage: 'You need to indicate if your content promotes yourself, a third party, or both.',
  },
  mediaTitle: { id: 'app.sml.promote.composer.tiktok.media.title', defaultMessage: 'The video for TikTok' },
  mediaLength: {
    id: 'app.sml.promote.composer.tiktok.media.length',
    defaultMessage: 'Length {length}. This account takes videos of up to {max}.',
  },
  mediaLengthOnly: {
    id: 'app.sml.promote.composer.tiktok.media.lengthOnly',
    defaultMessage: 'Length {length}.',
  },
  consentMusic: {
    id: 'app.sml.promote.composer.tiktok.consent.music',
    defaultMessage: "By posting, you agree to TikTok's <music>Music Usage Confirmation</music>.",
  },
  consentBranded: {
    id: 'app.sml.promote.composer.tiktok.consent.branded',
    defaultMessage:
      "By posting, you agree to TikTok's <branded>Branded Content Policy</branded> and <music>Music Usage Confirmation</music>.",
  },
  processing: {
    id: 'app.sml.promote.composer.tiktok.processing',
    defaultMessage: 'TikTok can take a few minutes to process the video before it shows on your profile.',
  },
  needsPrivacy: {
    id: 'app.sml.promote.composer.tiktok.needsPrivacy',
    defaultMessage: 'Choose who can see this post to publish it.',
  },
  needsDisclosure: {
    id: 'app.sml.promote.composer.tiktok.needsDisclosure',
    defaultMessage: 'Choose Your brand or Branded content to publish it.',
  },
  checkAgain: { id: 'app.sml.promote.composer.tiktok.checkAgain', defaultMessage: 'Check again' },
  openChannels: { id: 'app.sml.promote.composer.tiktok.openConnections', defaultMessage: 'Open Connections' },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  openChannelsNative: { id: 'app.sml.promote.composer.tiktok.openChannels', defaultMessage: 'Open Channels' },
  seePlans: { id: 'app.sml.promote.composer.tiktok.seePlans', defaultMessage: 'See plans' },
  blockTooManyPosts: {
    id: 'app.sml.promote.composer.tiktok.block.tooManyPosts',
    defaultMessage:
      'TikTok does not let this account post now. TikTok allows about 15 posts a day. Try again later, or remove TikTok from this post.',
  },
  blockBanned: {
    id: 'app.sml.promote.composer.tiktok.block.banned',
    defaultMessage: 'TikTok does not let this account post. Ask TikTok why, or remove TikTok from this post.',
  },
  blockActiveUserCap: {
    id: 'app.sml.promote.composer.tiktok.block.activeUserCap',
    defaultMessage:
      'TikTok cannot take more posts from MUSIC24 today. Try again tomorrow, or remove TikTok from this post.',
  },
  blockNoPrivacy: {
    id: 'app.sml.promote.composer.tiktok.block.noPrivacy',
    defaultMessage: 'TikTok offers no privacy level for this account now. Try again later.',
  },
  blockReconnect: {
    id: 'app.sml.promote.composer.tiktok.block.reconnectConnections',
    defaultMessage: 'TikTok refused the login. Reconnect TikTok in Connections, then come back.',
  },
  // The native apps read the old Channels copy (navigation v2 renamed the page Connections).
  blockReconnectNative: {
    id: 'app.sml.promote.composer.tiktok.block.reconnect',
    defaultMessage: 'TikTok refused the login. Reconnect TikTok in Channels, then come back.',
  },
  blockUnavailable: {
    id: 'app.sml.promote.composer.tiktok.block.unavailable',
    defaultMessage: 'We could not read your TikTok account. Check again in a moment.',
  },
  blockWatermark: {
    id: 'app.sml.promote.composer.tiktok.block.watermark',
    defaultMessage:
      'This video has the MUSIC24 mark of the Free plan. TikTok does not allow promotional marks. Upgrade, then make a new version of the video, or attach your own video.',
  },
  blockTooLong: {
    id: 'app.sml.promote.composer.tiktok.block.tooLong',
    defaultMessage: 'The video is {length}. This TikTok account takes videos of up to {max}. Attach a shorter video.',
  },
});
